import { useState, useEffect } from "react";
import { FileText, Link2, AlertTriangle, Eye, Loader2, Plus, X, GripVertical } from "lucide-react";
import { isPast, differenceInDays } from "date-fns";
import { db } from "@/lib/supabaseUntyped";
import { toast } from "sonner";
import { cn, slugify } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { UnifiedDocumentLinkDialog, type DocConfig } from "@/components/shared/UnifiedDocumentLinkDialog";
import { useDocumentPreview } from "@/contexts/DocumentPreviewContext";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

interface DemarcheDocument {
  id: string;
  label: string;
  doc_type: string | null;
  is_required: boolean;
  is_completed: boolean;
  validity_rule: string | null;
  document_id: string | null;
  sort_order: number;
  document?: {
    id: string;
    title: string;
    file_name: string;
    file_path?: string;
    mime_type?: string;
    expires_at: string | null;
  } | null;
}

interface DemarcheDocumentsListProps {
  demarcheId: string;
  documents: DemarcheDocument[];
  onUpdate: () => void;
}

interface SortableDocumentItemProps {
  doc: DemarcheDocument;
  isLoading: boolean;
  validity: { type: 'expired' | 'warning'; message: string } | null;
  onToggleComplete: (doc: DemarcheDocument) => void;
  onLinkDocument: (doc: DemarcheDocument) => void;
  onViewDocument: (doc: DemarcheDocument) => void;
  onUnlinkDocument: (doc: DemarcheDocument) => void;
  onDeleteDocItem: (docId: string) => void;
}

function SortableDocumentItem({
  doc,
  isLoading,
  validity,
  onToggleComplete,
  onLinkDocument,
  onViewDocument,
  onUnlinkDocument,
  onDeleteDocItem,
}: SortableDocumentItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: doc.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        "flex items-start gap-2 p-3 rounded-lg border transition-colors",
        doc.is_completed 
          ? "bg-primary/5 border-primary/20" 
          : "bg-muted/30 border-border",
        isDragging && "shadow-lg ring-2 ring-primary/20"
      )}
    >
      <button
        {...attributes}
        {...listeners}
        className="touch-none p-1 text-muted-foreground hover:text-foreground cursor-grab active:cursor-grabbing"
      >
        <GripVertical className="w-4 h-4" />
      </button>

      <Checkbox
        checked={doc.is_completed}
        onCheckedChange={() => onToggleComplete(doc)}
        disabled={isLoading}
        className="mt-0.5"
      />

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1 flex-wrap">
          <span className={cn(
            "text-sm font-medium",
            doc.is_completed && "line-through text-muted-foreground"
          )}>
            {doc.label}
          </span>
          {!doc.is_required && (
            <Badge variant="outline" className="text-[10px]">Optionnel</Badge>
          )}
          {validity && (
            <Badge 
              variant={validity.type === 'expired' ? 'destructive' : 'outline'}
              className="text-[10px] gap-1"
            >
              <AlertTriangle className="w-2.5 h-2.5" />
              {validity.message}
            </Badge>
          )}
        </div>

        {doc.validity_rule && !doc.document && (
          <p className="text-xs text-muted-foreground">
            Validité requise : {doc.validity_rule}
          </p>
        )}

        {doc.document && (
          <div className="flex items-center gap-2 mt-1 flex-wrap">
            <FileText className="w-3 h-3 text-primary" />
            <button 
              onClick={() => onViewDocument(doc)}
              className="text-xs text-primary truncate hover:underline"
            >
              {doc.document.title || doc.document.file_name}
            </button>
            <Button
              variant="ghost"
              size="sm"
              className="h-5 px-1 text-muted-foreground hover:text-foreground"
              onClick={() => onViewDocument(doc)}
            >
              <Eye className="w-3 h-3" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="h-5 px-1 text-xs text-muted-foreground hover:text-destructive"
              onClick={() => onUnlinkDocument(doc)}
              disabled={isLoading}
            >
              Délier
            </Button>
          </div>
        )}
      </div>

      <div className="flex items-center gap-1 shrink-0">
        {!doc.document && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => onLinkDocument(doc)}
            disabled={isLoading}
          >
            <Link2 className="w-3.5 h-3.5 mr-1" />
            Lier
          </Button>
        )}
        {!doc.is_required && (
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-muted-foreground hover:text-destructive"
            onClick={() => onDeleteDocItem(doc.id)}
            disabled={isLoading}
            title="Supprimer de la liste"
          >
            <X className="w-4 h-4" />
          </Button>
        )}
      </div>
    </div>
  );
}

export function DemarcheDocumentsList({ demarcheId, documents, onUpdate }: DemarcheDocumentsListProps) {
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [addDocDialogOpen, setAddDocDialogOpen] = useState(false);
  const [newDocLabel, setNewDocLabel] = useState("");
  const [isAddingDoc, setIsAddingDoc] = useState(false);
  const [selectedDocItem, setSelectedDocItem] = useState<DemarcheDocument | null>(null);
  const [updatingId, setUpdatingId] = useState<string | null>(null);
  const [localDocuments, setLocalDocuments] = useState<DemarcheDocument[]>(documents);
  const [isDragging, setIsDragging] = useState(false);
  const { openDocument } = useDocumentPreview();

  // Keep local state in sync with props when documents change
  useEffect(() => {
    if (!isDragging) {
      // Check if IDs match AND if document_id values match (for unlink detection)
      const localIds = new Set(localDocuments.map(d => d.id));
      const propsIds = new Set(documents.map(d => d.id));
      const idsMatch = localIds.size === propsIds.size && [...localIds].every(id => propsIds.has(id));
      
      // Also check if any document_id changed (unlink/link operations)
      const documentLinksMatch = idsMatch && documents.every(propDoc => {
        const localDoc = localDocuments.find(ld => ld.id === propDoc.id);
        return localDoc && localDoc.document_id === propDoc.document_id;
      });
      
      if (!idsMatch || !documentLinksMatch) {
        setLocalDocuments(documents);
      }
    }
  }, [documents, isDragging]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragStart = () => {
    setIsDragging(true);
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    setIsDragging(false);
    const { active, over } = event;
    
    if (!over || active.id === over.id) return;

    const oldIndex = localDocuments.findIndex(d => d.id === active.id);
    const newIndex = localDocuments.findIndex(d => d.id === over.id);
    
    const newOrder = arrayMove(localDocuments, oldIndex, newIndex);
    setLocalDocuments(newOrder);

    // Update sort_order in database
    try {
      const updates = newOrder.map((doc, index) => ({
        id: doc.id,
        sort_order: index,
      }));

      for (const update of updates) {
        await db
          .from('demarche_documents')
          .update({ sort_order: update.sort_order })
          .eq('id', update.id);
      }
    } catch (error) {
      console.error('Error updating document order:', error);
      toast.error("Erreur lors de la réorganisation");
      setLocalDocuments(documents); // Revert to original order
    }
  };

  const handleAddDocument = async () => {
    if (!newDocLabel.trim()) {
      toast.error("Veuillez saisir un nom pour le document");
      return;
    }

    setIsAddingDoc(true);
    try {
      const maxOrder = localDocuments.length > 0 
        ? Math.max(...localDocuments.map(d => d.sort_order)) 
        : 0;

      const { error } = await db
        .from('demarche_documents')
        .insert({
          demarche_id: demarcheId,
          label: newDocLabel.trim(),
          is_required: false,
          is_completed: false,
          sort_order: maxOrder + 1,
        });

      if (error) throw error;

      toast.success("Document ajouté");
      setNewDocLabel("");
      setAddDocDialogOpen(false);
      onUpdate();
    } catch (error) {
      console.error('Error adding document:', error);
      toast.error("Erreur lors de l'ajout");
    } finally {
      setIsAddingDoc(false);
    }
  };

  const handleDeleteDocItem = async (docId: string) => {
    setUpdatingId(docId);
    try {
      const { error } = await db
        .from('demarche_documents')
        .delete()
        .eq('id', docId);

      if (error) throw error;
      toast.success("Document supprimé de la liste");
      onUpdate();
    } catch (error) {
      console.error('Error deleting document item:', error);
      toast.error("Erreur lors de la suppression");
    } finally {
      setUpdatingId(null);
    }
  };

  const handleToggleComplete = async (doc: DemarcheDocument) => {
    setUpdatingId(doc.id);
    try {
      const { error } = await db
        .from('demarche_documents')
        .update({ is_completed: !doc.is_completed })
        .eq('id', doc.id);

      if (error) throw error;
      onUpdate();
    } catch (error) {
      console.error('Error updating document:', error);
      toast.error('Erreur lors de la mise à jour');
    } finally {
      setUpdatingId(null);
    }
  };

  const handleLinkDocument = (doc: DemarcheDocument) => {
    setSelectedDocItem(doc);
    setLinkDialogOpen(true);
  };

  const handleViewDocument = (doc: DemarcheDocument) => {
    if (!doc.document) return;
    openDocument({
      id: doc.document.id,
      title: doc.document.title || doc.document.file_name,
      file_path: doc.document.file_path,
      mime_type: doc.document.mime_type,
    } as any);
  };

  const handleUnlinkDocument = async (doc: DemarcheDocument) => {
    setUpdatingId(doc.id);
    try {
      const { error } = await db
        .from('demarche_documents')
        .update({ 
          document_id: null,
          is_completed: false 
        })
        .eq('id', doc.id);

      if (error) throw error;
      toast.success('Liaison supprimée');
      onUpdate();
    } catch (error) {
      console.error('Error unlinking document:', error);
      toast.error('Erreur lors de la suppression');
    } finally {
      setUpdatingId(null);
    }
  };

  const getValidityStatus = (doc: DemarcheDocument) => {
    if (!doc.document?.expires_at) return null;
    
    const expiresAt = new Date(doc.document.expires_at);
    const daysUntil = differenceInDays(expiresAt, new Date());
    
    if (isPast(expiresAt)) {
      return { type: 'expired' as const, message: 'Expiré' };
    }
    if (daysUntil <= 30) {
      return { type: 'warning' as const, message: `Expire dans ${daysUntil}j` };
    }
    return null;
  };

  const getDocConfig = (doc: DemarcheDocument): DocConfig => ({
    key: slugify(doc.label),
    label: doc.label,
    doc_type: doc.doc_type || undefined,
  });

  return (
    <>
      <div className="space-y-2">
        {localDocuments.length === 0 ? (
          <div className="text-center py-4">
            <p className="text-sm text-muted-foreground mb-3">Aucun document requis</p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setAddDocDialogOpen(true)}
            >
              <Plus className="w-4 h-4 mr-2" />
              Ajouter un document
            </Button>
          </div>
        ) : (
          <>
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={localDocuments.map(d => d.id)}
                strategy={verticalListSortingStrategy}
              >
                {localDocuments.map((doc) => (
                  <SortableDocumentItem
                    key={doc.id}
                    doc={doc}
                    isLoading={updatingId === doc.id}
                    validity={getValidityStatus(doc)}
                    onToggleComplete={handleToggleComplete}
                    onLinkDocument={handleLinkDocument}
                    onViewDocument={handleViewDocument}
                    onUnlinkDocument={handleUnlinkDocument}
                    onDeleteDocItem={handleDeleteDocItem}
                  />
                ))}
              </SortableContext>
            </DndContext>

            <Button
              variant="ghost"
              size="sm"
              className="w-full mt-2 border border-dashed text-muted-foreground hover:text-foreground"
              onClick={() => setAddDocDialogOpen(true)}
            >
              <Plus className="w-4 h-4 mr-2" />
              Ajouter un document personnalisé
            </Button>
          </>
        )}
      </div>

      {/* Add Document Dialog */}
      <Dialog open={addDocDialogOpen} onOpenChange={setAddDocDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="w-5 h-5 text-primary" />
              Ajouter un document
            </DialogTitle>
            <DialogDescription>
              Ajoutez un document personnalisé à cette démarche
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="docLabel">Nom du document</Label>
              <Input
                id="docLabel"
                placeholder="Ex: Justificatif de domicile"
                value={newDocLabel}
                onChange={(e) => setNewDocLabel(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !isAddingDoc) {
                    handleAddDocument();
                  }
                }}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddDocDialogOpen(false)}>
              Annuler
            </Button>
            <Button onClick={handleAddDocument} disabled={isAddingDoc || !newDocLabel.trim()}>
              {isAddingDoc ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Ajout...
                </>
              ) : (
                <>
                  <Plus className="w-4 h-4 mr-2" />
                  Ajouter
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Unified Document Link Dialog */}
      {selectedDocItem && (
        <UnifiedDocumentLinkDialog
          open={linkDialogOpen}
          onOpenChange={(open) => {
            setLinkDialogOpen(open);
            if (!open) setSelectedDocItem(null);
          }}
          entityType="demarche_document"
          entityId={selectedDocItem.id}
          docConfig={getDocConfig(selectedDocItem)}
          linkTableName="demarche_documents"
          onSuccess={onUpdate}
          defaultTab="link"
          showValidityDates={true}
          customHeader={
            <div className="p-3 bg-muted/50 rounded-lg mb-4">
              <p className="text-sm font-medium">{selectedDocItem.label}</p>
              {selectedDocItem.validity_rule && (
                <p className="text-xs text-muted-foreground mt-1">
                  Validité requise : {selectedDocItem.validity_rule}
                </p>
              )}
            </div>
          }
        />
      )}
    </>
  );
}
